import { t } from '@lingui/core/macro';
import { MembersAvatarStack } from '@src/components/conversation/MembersAvatarStack';
import { ProjectGitChecksDialog } from '@src/components/project-home/ProjectGitChecksDialog';
import { ProjectGitChip, type GitCheck } from '@src/components/project-home/ProjectGitChip';
import { ProjectHomeMenu } from '@src/components/project-home/ProjectHomeMenu';
import { ProjectSetupPage } from '@src/components/project-setup/ProjectSetupPage';
import { ProjectCloudLinkButton } from '@src/components/project-home/ProjectCloudLinkButton';
import { ProjectGitShareToggle } from '@src/components/project-home/ProjectGitShareToggle';
import { ProjectPublishedButton } from '@src/components/project-home/ProjectPublishedButton';
import { PublishProjectDialog } from '@src/components/project-home/PublishProjectDialog';
import { QuickCreatePanel, useQuickCreatePick } from '@src/components/quick-create';
import { ProjectAgentsStrip } from '@src/components/agents/ProjectAgentsStrip';
import type { PanelHandlers } from '@src/components/quick-create';
import { HomeCustomizationCard } from './HomeCustomizationCard';
import { ProjectLanguageCard } from './ProjectLanguageCard';
import { ProjectDataSources } from './ProjectDataSources';
import { ProjectApps } from './ProjectApps';
import { ProjectDependenciesCard } from './ProjectDependenciesCard';
import { QuickCreateModal } from '@src/components/quick-create/QuickCreateModal';
import { Button } from '@src/components/ui/button';
import { Plus } from 'lucide-react';
import { useProject } from '@sdk/react/hooks';
import { VIBE_AGENTS_TAG, VibeAgentsCard } from './VibeAgentsCard';
import { useHighlight } from '@src/components/wiki-tip/highlight';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@src/components/ui/tabs';
import { useContext as useDataContext } from '@src/hooks/useContext';
import { useShareRoles } from '@src/hooks/use-share-roles';
import { useTerminalStripController } from '@src/tabs/useTerminalStripController';
import { Project, TypeId } from '@sdk';
import { tagAttrs } from '@src/tags/tag-attrs';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';
import { useUiActionRequest } from '@src/navigation/ui-actions';

/** Journey anchor for the session launcher (`?highlight=NewSession`). */
const NEW_SESSION_TAG = 'NewSession';

interface ProjectHomeProps {
  /** Pin spawned shells/processes to this project; otherwise the active project. */
  spawnProjectId?: string | null;
  /** Render only the Overview body with no tab bar — the terminal empty state,
   *  whose whole point is to start something. The landing shows both tabs. */
  createOnly?: boolean;
  /** Show the project's setup page (`…/project-setup`) under the header instead of Overview/Customize. */
  setupPage?: boolean;
  /** Hub-owned body rendered inside the shared Project Home shell. When set,
   *  local Git/setup/create controls are omitted; the Hub body owns content. */
  cloudContent?: React.ReactNode;
}

/** Launchers share the established terminal/Vibe opener controller. */
const SessionTiles: React.FC<{ project: Project | null; panelProps: PanelHandlers }> = ({ project, panelProps }) => {
  const { t } = useLingui();
  const { modals, isTabCreationPending, openers } = useTerminalStripController({ spawnProjectId: project?.id });
  const tileFor = (id: string, label: string, wikiword: string) => {
    const opener = openers.find((o) => o.id === id);
    if (!opener) return [];
    return [
      {
        key: id,
        Icon: opener.Icon,
        label,
        wikiword,
        disabled: isTabCreationPending,
        onClick: () => opener.onActivate(),
      },
    ];
  };
  return (
    <div data-testid="project-home-start-session" {...tagAttrs(NEW_SESSION_TAG, 'button')}>
      <QuickCreatePanel
        {...panelProps}
        project={project}
        sections={['chat']}
        chatTitle={<Trans>Launch</Trans>}
        leadingSessionTiles={tileFor('vibe', t`Vibe`, 'Vibe')}
        extraSessionTiles={tileFor('terminal', t`Terminal`, 'Terminal sessions')}
      />
      {modals}
    </div>
  );
};

const Overview: React.FC<{ project: Project | null; panelProps: PanelHandlers; dependencies: boolean }> = ({
  project,
  panelProps,
  dependencies,
}) => {
  const [newOpen, setNewOpen] = useState(false);
  return (
    <div className="flex flex-col gap-6" data-testid="project-home-overview">
      {project && <SessionTiles project={project} panelProps={panelProps} />}
      <section>
        <h3 className="mb-2 text-sm font-semibold">
          <Trans>New</Trans>
        </h3>
        <Button
          variant="outline"
          size="icon"
          aria-label={t`Create new`}
          data-testid="project-home-new"
          disabled={!project}
          onClick={() => setNewOpen(true)}
        >
          <Plus className="size-5" />
        </Button>
      </section>
      {project && <ProjectApps project={project} />}
      {project && <ProjectAgentsStrip projectId={project.id} presentation="browse" />}
      {/* The project's flow.json dependencies — not on the terminal empty state. */}
      {project && dependencies && <ProjectDependenciesCard project={project} />}
      {project && <ProjectDataSources projectId={project.id} />}
      <QuickCreateModal
        open={newOpen}
        onOpenChange={setNewOpen}
        panelProps={panelProps}
        project={project}
        sections={['build', 'write', 'connect', 'project']}
        showAddExisting
        allUserTypes
      />
    </div>
  );
};

/** Which tab hosts which tag word — see the `?highlight=` effect below.
 *  Add an entry whenever a card on a non-default tab takes `tagAttrs`. */
const TAB_FOR_TAG: Record<string, string> = {
  [VIBE_AGENTS_TAG]: 'customize',
};

/**
 * ProjectHome — the project's landing surface, shown wherever a project has no
 * open content: the terminal body's empty state (no terminal sessions) and the
 * project-home content slot (no asset/item selected). The one surface that is
 * unambiguously "the project itself" rather than content inside it.
 *
 * Organized into two tabs:
 *   - **Overview**  — Launch, New, existing agents and data sources.
 *   - **Customize** — home title/background + the vibe agents layered on.
 *
 * A third, **Secrets**, was retired: it was one of five surfaces over the same
 * declarations, and the Connections screen is now the only one.
 */
export const ProjectHome: React.FC<ProjectHomeProps> = ({
  spawnProjectId,
  createOnly = false,
  setupPage = false,
  cloudContent,
}) => {
  const dataCtx = useDataContext();
  const cloudMode = cloudContent !== undefined;

  // Resolve the target project (explicit spawn pin, else the active project).
  const projectId = spawnProjectId ?? dataCtx.project?.id ?? null;
  const projectTypeId = useMemo(() => (projectId ? new TypeId(Project.type, projectId) : null), [projectId]);
  const shareRoles = useShareRoles();

  // The dialogs the New picker defers to. Hosted here rather than in the panel
  // so they outlive whatever the tile click dismisses.
  const { project: resolvedProject } = useProject(projectTypeId);
  const { panelProps, dialogs } = useQuickCreatePick(resolvedProject ?? null);

  // Customize cards are project-entity bound — only when the resolved
  // project is the active one (they read/write live Project state).
  const project = dataCtx.project?.id === projectId ? dataCtx.project : null;
  const [gitChecks, setGitChecks] = useState<GitCheck[] | null>(null);
  const [publishOpen, setPublishOpen] = useState(false);
  // "publish this project" (smart navigation): the same popup the publish button opens.
  useUiActionRequest(['publish-dialog'], () => setPublishOpen(true));
  // The invite pane's opener, held while the publish popup stands in front of it.
  const resumeInvite = useRef<(() => void) | null>(null);
  // Invite branches on whether the Project has a hub row. Published: the invite
  // pane opens and invites are membership grants — no publish checks. Not
  // published: there is nothing to grant on yet, so the publish popup opens
  // INSTEAD of the pane — and once it publishes, the pane opens, because
  // publishing was a step of the invite, not the end of it.
  const beforeProjectInvite = useMemo<((proceed: () => void) => boolean) | undefined>(() => {
    if (!project || cloudMode) return undefined;
    return (proceed) => {
      if (project.remote === true) return true;
      resumeInvite.current = proceed;
      setPublishOpen(true);
      return false;
    };
  }, [cloudMode, project]);
  const onPublishOpenChange = useCallback((next: boolean) => {
    setPublishOpen(next);
    if (!next) resumeInvite.current = null;
  }, []);
  const onProjectPublished = useCallback(() => {
    const proceed = resumeInvite.current;
    resumeInvite.current = null;
    setPublishOpen(false);
    proceed?.();
  }, []);
  const overview = <Overview project={resolvedProject ?? null} panelProps={panelProps} dependencies={!createOnly} />;

  // A `?highlight=` target that lives on a tab we aren't showing would never
  // mount, so the generic TagHighlightObserver would find nothing — open the
  // owning tab instead. Each tab declares the tag words it hosts.
  const [tab, setTab] = useState('create');
  const highlight = useHighlight();
  useEffect(() => {
    const owner = highlight ? TAB_FOR_TAG[highlight] : undefined;
    if (owner) setTab(owner);
  }, [highlight]);

  return (
    <div className="flex h-full flex-col">
      {/* Project header: git state on the left, roster + invite on the right.
          The roster names itself (avatars / "No members" / Invite), so a
          separate "Members" caption was one label too many. */}
      {projectTypeId && (
        <div
          className="flex flex-wrap items-center justify-between gap-2 border-b border-border/50 px-4 py-2"
          data-testid="project-home-members"
        >
          <div className="flex min-w-0 flex-wrap items-center gap-2 [&>*]:shrink-0">
            {cloudMode ? (
              <>
                <span className="truncate text-sm font-medium">
                  {project?.displayName ?? project?.name ?? 'Project'}
                </span>
                <ProjectPublishedButton projectId={projectTypeId.id} />
              </>
            ) : (
              <>
                {project && <ProjectHomeMenu project={project} />}
                <ProjectGitChip projectTypeId={projectTypeId} onChecked={setGitChecks} />
                {project && <ProjectCloudLinkButton project={project} />}
                {project && <ProjectGitShareToggle project={project} />}
                <ProjectPublishedButton projectId={projectTypeId.id} />
              </>
            )}
          </div>
          <MembersAvatarStack
            typeId={projectTypeId}
            allowInviteLink
            showInviteButton
            beforeInvite={beforeProjectInvite}
            inviteRoles={shareRoles}
            uiAction="invite-members"
          />
        </div>
      )}
      {!cloudMode && gitChecks && (
        <ProjectGitChecksDialog
          open
          onOpenChange={(next) => !next && setGitChecks(null)}
          checks={gitChecks}
          project={project}
        />
      )}
      <div className="min-h-0 flex-1 overflow-y-auto">
        <div className="mx-auto flex w-full flex-col gap-6 px-4 py-6">
          {cloudMode ? (
            cloudContent
          ) : createOnly ? (
            overview
          ) : setupPage && projectId ? (
            <ProjectSetupPage projectId={projectId} projectName={resolvedProject?.name ?? project?.name ?? ''} />
          ) : (
            <Tabs value={tab} onValueChange={setTab} data-testid="project-home-tabs">
              <TabsList>
                <TabsTrigger value="create" data-testid="project-home-tab-create">
                  <Trans>Overview</Trans>
                </TabsTrigger>
                <TabsTrigger value="customize" data-testid="project-home-tab-customize">
                  <Trans>Customize</Trans>
                </TabsTrigger>
              </TabsList>

              <TabsContent value="create">{overview}</TabsContent>

              <TabsContent value="customize" className="flex flex-col gap-6">
                {project && (
                  <>
                    <ProjectLanguageCard project={project} />
                    <HomeCustomizationCard project={project} />
                    <VibeAgentsCard project={project} />
                  </>
                )}
              </TabsContent>
            </Tabs>
          )}
        </div>
      </div>

      {!cloudMode && project && (
        <PublishProjectDialog
          project={project}
          open={publishOpen}
          onOpenChange={onPublishOpenChange}
          onPublished={onProjectPublished}
        />
      )}
      {!cloudMode && dialogs}
    </div>
  );
};
